import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlowMobilePlanConfig } from "../..";
import { ConfigureCallFlow } from "../ConfigureCallFlow";
import { ConfigurationsEmptyState } from "./components/ConfigurationsEmptyState";
import { PlanRow } from "./components/PlanRow";
import { VoiceMessagesSettings } from "./components/VoiceMessagesSettings";
import { FlowMobileUsageTab } from "./components/FlowMobileUsageTab";

const ORANGE = "#FB8E00";

type FlowMobileConfigTab = "configurations" | "plans" | "usage";

const tabSx = {
  minHeight: 0,
  py: 1.25,
  px: 2,
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  color: "var(--fx-surface-textsecondary, #64748B)",
  "&.Mui-selected": {
    color: ORANGE,
  },
};

const tabsSx = {
  minHeight: 0,
  px: 3,
  borderBottom: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
  "& .MuiTabs-indicator": {
    backgroundColor: ORANGE,
    height: 2,
  },
};

export type CompanyNumber = {
  uid: string;
  companyId: number;
  name: string | null;
  phoneNumber: string;
  status: string;
  source: string;
  provider: string;
  providerPhoneNumberSid?: string | null;
  portingUid?: string | null;
  buyingUid?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

interface NumbersConfigurationProps {
  companyNumbers: CompanyNumber[];
  plans: FlowMobilePlanConfig[];
}

export function NumbersConfiguration({ companyNumbers, plans }: NumbersConfigurationProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FlowMobileConfigTab>("plans");

  const companyNumbersByUid = useMemo(() => {
    return new Map<string, CompanyNumber>(
      companyNumbers
        .filter((companyNumber: CompanyNumber) => {
          return ["ACTIVE", "PENDING", "INACTIVE"].includes(companyNumber.status);
        })
        .map((companyNumber: CompanyNumber) => {
          return [companyNumber.uid, companyNumber];
        }),
    );
  }, [companyNumbers]);

  const unassignedInactiveNumbers = useMemo<CompanyNumber[]>(() => {
    const assignedExternalIds = new Set(plans.map((plan) => plan.externalId).filter((externalId): externalId is string => Boolean(externalId)));

    return Array.from(companyNumbersByUid.values()).filter((companyNumber) => {
      const isInactive = companyNumber.status === "INACTIVE";

      const isNotAssigned = !assignedExternalIds.has(companyNumber.uid);

      return isInactive && isNotAssigned;
    });
  }, [plans, companyNumbersByUid]);

  const availableNumbers = useMemo<CompanyNumber[]>(() => {
    return Array.from(companyNumbersByUid.values()).filter((companyNumber) => {
      return companyNumber.status === "ACTIVE" || companyNumber.status === "INACTIVE";
    });
  }, [companyNumbersByUid]);

  const hasActivePlans = plans.length > 0;

  const assigned = useMemo(() => {
    return plans.filter((plan) => {
      if (!plan.externalId) return false;

      const companyNumber = companyNumbersByUid.get(plan.externalId);

      return companyNumber?.status === "ACTIVE";
    }).length;
  }, [plans, companyNumbersByUid]);

  const hasCompletedSetup = assigned > 0;
  const shouldShowCallFlow = hasActivePlans && hasCompletedSetup;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        mx: "auto",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 1.8,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 900,
                color: "var(--fx-surface-text, #0F172A)",
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
              }}
            >
              Flow Mobile
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                color: "var(--fx-surface-textsecondary, #64748B)",
                fontWeight: 500,
                mt: 0.8,
                maxWidth: 620,
                lineHeight: 1.35,
              }}
            >
              Assign and manage the phone numbers connected to your active Flow Mobile plans.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: "var(--fx-surface-textsecondary, #64748B)",
                fontWeight: 500,
              }}
            >
              Need an extra line?
            </Typography>

            <PrimaryButton variant="contained" fullWidth sx={{ borderRadius: "8px" }} onClick={() => navigate("/business/apps-center")}>
              Add extra line
            </PrimaryButton>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value: FlowMobileConfigTab) => setActiveTab(value)}
        aria-label="Flow Mobile configuration tabs"
        variant="fullWidth"
        sx={{ ...tabsSx, flexShrink: 0 }}
      >
        <Tab disableRipple value="plans" label="Plans" sx={tabSx} />
        <Tab disableRipple value="configurations" label="Settings" sx={tabSx} />
        <Tab disableRipple value="usage" label="Usage" sx={tabSx} />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {activeTab === "configurations" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {shouldShowCallFlow ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  pt: 1,
                  pb: 3,
                }}
              >
                <ConfigureCallFlow />
                <VoiceMessagesSettings />
              </Box>
            ) : (
              <ConfigurationsEmptyState onGoToPlans={() => setActiveTab("plans")} />
            )}
          </Box>
        )}

        {activeTab === "plans" && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              px: 3,
              pt: 2,
              pb: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
            }}
          >
            {plans.length === 0 ? (
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  borderRadius: "12px",
                  border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
                  bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.015)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 900,
                    color: "var(--fx-surface-text, #0F172A)",
                  }}
                >
                  No active Flow Mobile plans
                </Typography>

                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    mt: 0.45,
                  }}
                >
                  Active or completed subscriptions will appear here once available.
                </Typography>
              </Box>
            ) : (
              plans.map((plan) => {
                return (
                  <PlanRow
                    key={plan.subscriptionId}
                    plan={plan}
                    plans={plans}
                    companyNumbersByUid={companyNumbersByUid}
                    availableNumbers={availableNumbers}
                  />
                );
              })
            )}
          </Box>
        )}

        {activeTab === "usage" && <FlowMobileUsageTab companyNumbers={companyNumbers} />}
      </Box>
    </Box>
  );
}
